import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getStructureSlotting, type StructureSlottingInsight } from "../api/rest";
import { groupSlottingInsights, insightNodeLabel } from "./slottingInsights";
import { CollapsibleSection } from "../app/layout/CollapsibleSection";
import run from "./InventoryRunPage.module.css";

const SEV_CLASS: Record<string, string> = {
  high: run.slotSevHigh,
  medium: run.slotSevMedium,
  info: run.slotSevInfo,
};

/** Whole-percent, decimal comma not needed (integer). */
function fmtPct0(x: number): string {
  return `${Math.round(x)}%`;
}

/**
 * ADR 172, slice S7: one run's slotting insights over the canonical structure graph — stray
 * loads (a counted load where storage isn't allowed), consolidation candidates (low-occupancy
 * storable subtrees to reclaim) and putaway capacity (known-empty storable spaces). Grouped by
 * urgency; run-scoped from the run's own warehouseId. Self-hides when there is nothing to advise.
 */
export function SlottingInsightsSection({ siteId, warehouseId, runId }: {
  siteId: string;
  warehouseId: string;
  runId: string;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["structure-slotting", siteId, warehouseId, runId],
    queryFn: () => getStructureSlotting(siteId, warehouseId, runId),
    retry: 0,
  });

  const groups = groupSlottingInsights(query.data?.insights ?? []);
  if (groups.length === 0) return null;
  const s = query.data!.summary;

  const kindLabel = (kind: string) => t(`inventory.structureOccupancy.kind.${kind}`);

  function metric(i: StructureSlottingInsight): string {
    if (i.type === "consolidation") {
      return t("inventory.slotting.metric.consolidation", {
        pct: fmtPct0(i.occupancyPct ?? 0),
        reclaim: i.reclaimableSpaces ?? 0,
      });
    }
    if (i.type === "putaway_capacity") {
      return t("inventory.slotting.metric.putaway", { empty: i.emptyStorableSpaces ?? 0 });
    }
    return t("inventory.slotting.metric.stray", { address: i.address ?? "" });
  }

  return (
    <CollapsibleSection
      id="run.slotting"
      title={t("inventory.slotting.title")}
      data-testid="slotting-insights-section"
    >
      <p className={run.note}>{t("inventory.slotting.intro")}</p>
      <div className={run.slotSummary}>
        <span>{t("inventory.slotting.summary.stray")}: <b>{s.strayLoads}</b></span>
        <span>{t("inventory.slotting.summary.consolidation")}: <b>{s.consolidationCandidates}</b></span>
        <span>{t("inventory.slotting.summary.empty")}: <b>{s.emptyStorableSpaces}</b></span>
      </div>
      {groups.map((g) => (
        <div className={run.slotGroup} key={g.type}>
          <div className={run.slotGroupTitle}>{t(`inventory.slotting.group.${g.type}`)}</div>
          <div className={run.slotList} role="list">
            {g.items.map((i, idx) => (
              <div className={run.slotRow} role="listitem" key={`${i.nodeId}-${i.address ?? idx}`}>
                <span className={`${run.slotSev} ${SEV_CLASS[i.severity] ?? ""}`} aria-hidden />
                <span className={run.slotKind}>{kindLabel(i.kind)}</span>
                <span className={run.slotName}>{insightNodeLabel(i)}</span>
                <span className={run.slotMetric}>{metric(i)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </CollapsibleSection>
  );
}
